<template>
  <form id="id-new-convo-form">
    <ConveyRequiredStatement class="mb-2" />
    <p v-if="errors.length" class="text-danger">
      <b>Please correct the following error(s):</b>
      <ul><li :key="error" v-for="error in errors">{{ error }}</li></ul>
    </p>
    <div class="form-group pb-3">
      <h6 v-if="approximateAddress" class="mb-0">Approximated Address: {{ approximateAddress }}</h6>
      <div class="mb-2" v-if="lat && lng">
        <GoogleMap :geography="[{ lat, lng }]" style="height:300px" />
      </div>

      <div class="row">
        <label
          class="required-field-label"
          for="id-newconvo-target-number"
          style="float: left;"
        >
          Recipient Phone Number
        </label>
      </div>
      <div class="row" v-if="internationalDialCodeEnabled">
        <div class="col-3">
          <v-select v-model="dialCode" :options="dialCodeSelection" :clearable="false">
            <template #search="{ attributes, events }">
              <input class="vs__search" v-bind="attributes" v-on="events" />
            </template>
          </v-select>
        </div>
        <div class="col-9">
          <input
            @keyup="focusOut"
            @blur="focusOut"
            id="id-recipientNumber"
            v-model="formattedRecipientNumber"
            type="text"
            placeholder="Enter target phone number"
            class="form-control bfh-phone"
            maxlength="12"
            required
          />
        </div>
      </div>
      <div class="row" v-else>
        <div class="col-12">
          <input
            @keyup="focusOut"
            @blur="focusOut"
            id="id-recipientNumber"
            v-model="formattedRecipientNumber"
            type="text"
            placeholder="Enter target phone number"
            class="form-control bfh-phone"
            maxlength="12"
            required
          />
        </div>
      </div>

      <div class="row">
        <label
          for="new-convo-initial-msg"
          style="float: left; margin-top:20px"
          :class="{
            'required-field-label': initialMessage && initialMessage.value === '--custom--'
          }"
        >
          Opening Message
        </label>
      </div>

      <v-select
        :options="messageTemplates"
        v-model="initialMessage"
        placeholder="-- Choose Optional Message to Send --"
      >
        <template #search="{ attributes, events }">
          <input class="vs__search" v-bind="attributes" v-on="events" />
        </template>
      </v-select>

      <div class="pt-1 d-none" id="id-custom-message">
        <textarea
          id="id-initialMessage-custom"
          style="resize:none;"
          class="form-control"
          rows="2"
          placeholder="Enter custom message to send..."
          v-model="customMessage"
        ></textarea>
      </div>

      <div class="row">
        <label for="new-convo-initial-msg" style="float: left; margin-top:20px">Target Language</label>
      </div>

      <v-select :options="textDialects" v-model="targetDialect" placeholder="-- Autodetect Language Dialect --">
        <template #search="{ attributes, events }">
          <input class="vs__search" v-bind="attributes" v-on="events" />
        </template>
      </v-select>

      <div class="row" v-if="numbers && numbers.length > 1">
        <label for="new-convo-initial-msg" style="float: left; margin-top:20px">Source Number</label>
      </div>

      <v-select
        v-if="numbers && numbers.length > 1"
        v-model="sourceNumber"
        :searchable="false"
        :options="numbers"
        :clearable="false"
      ></v-select>
    </div>
    <div class="modal-footer bg-light stretch-footer">
      <div class="text-right">
        <button
          type="button"
          class="btn btn-info btn-rounded"
          @click="validateForm"
          :disabled="httpPending || isLoadingNumbers || isLoadingTemplates"
        >
          <i v-if="httpPending" class="fa fa-spinner fa-spin"></i>
          <span v-else>Create Conversation</span>
        </button>
      </div>
    </div>
  </form>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions, mapGetters } from "vuex";
import { EventBus } from "../../events/eventBus";
import GoogleMap from "../GoogleMap";
import vSelect from "vue-select";
import formatter from "@/mixins/formatter";
import ConveyRequiredStatement from "@/components/ConveyRequiredStatement";

export default {
  name: "NewConversationForm",
  mixins: [formatter],
  components: {
    GoogleMap,
    vSelect,
    ConveyRequiredStatement
  },
  computed: {
    ...mapGetters("agency", ["textDialects", "currentAgencyId", "agencySettings"]),
    ...mapGetters("provider", ["providerImport"]),
    ...mapState({
      serverUrl: (state) => state.connection.serverUrl,
      wsOnly: (state) => state.connection.wsOnly,
      path: (state) => state.connection.path,
      namespace: (state) => state.connection.namespace,
      parser: (state) => state.connection.parser,
      currentUser: (state) => state.user.user,
      currentToken: (state) => state.user.token
    }),
    internationalDialCodeEnabled() {
      return this.agencySettings.enableInternational == "true";
    },
    sourceNumber: {
      get() {
        if (this.selectedSourceNumber) {
          return this.selectedSourceNumber;
        } else if (this.$cookie.get("x-pref-sourcenumber")) {
          const prefNumber = this.numbers.find((n) => n.number === this.$cookie.get("x-pref-sourcenumber"));
          if (prefNumber) {
            return prefNumber;
          } else {
            this.numbers.length > 0 ? this.numbers[0] : null;
          }
        } else {
         return this.numbers.length > 0 ? this.numbers[0] : null;
        }
      },
      set(v) {
        this.selectedSourceNumber = v;
      }
    }
  },
  data() {
    return {
      errors: [],
      dialCode: "+1",
      recipientNumber: null,
      initialMessage: null,
      customMessage: null,
      targetDialect: null,
      selectedSourceNumber: null,
      formattedRecipientNumber: null,
      preventNextIteration: false,
      approximateAddress: null,
      lat: null,
      lng: null,
      latlng_accuracy: null,
      altitude: null,
      altitude_accuracy: null,
      messageTemplates: [],
      numbers: [],
      httpPending: false,
      addSignature: false,
      isLoadingTemplates: false,
      isLoadingNumbers : false
    };
  },
  watch: {
    initialMessage: function (template) {
        const $ = window.$;
      if (template && template.value === "--custom--")
        $("#id-custom-message").removeClass("d-none");
      else
        $("#id-custom-message").addClass("d-none");
    }
  },
  methods: {
    ...mapActions("conversations", ["createConversation"]),
    async fetchTemplates() {
      try {
        this.isLoadingTemplates = true;
        const resp = await this.$ConveyApi.templatesApi.getUserMessageTemplates({
          params: { templateType: "all" }
        });
        this.messageTemplates = resp.data.data.map(t => ({ label: t.name, value: t.template_id, text: t.text }));
        this.messageTemplates.unshift({ label: "Enter a Custom Message", value: "--custom--", text: "" });
      } catch (e) {
        console.log('[NewConversationForm:fetchTemplates] ERROR: ', e);
      } finally {
        this.isLoadingTemplates = false;
      }
    },
    async fetchNumbers() {
      try {
        this.isLoadingNumbers = true;
        const resp = await this.$ConveyApi.numbersApi.getProviderNumbers({
          params: { status: 'active', capability: 'sms', inbound: true, outbound: true }
        });
        this.numbers = resp.data.data.map((item) => {
          return {
            ...item,
            label: item.label || this.prettyPhoneNumber(item.number),
          };
        });
      } catch (e) {
        console.log('[NewConversationForm:fetchNumbers] ERROR: ', e);
      } finally {
        this.isLoadingNumbers = false;
      }
    },
    async validateForm() {
      this.errors = [];

      try {
        this.recipientNumber = this.formattedRecipientNumber.replace(/[^0-9]/g, "");

        if (this.recipientNumber.length != 10)
          this.errors.push("Please enter a valid 10-digit phone number (123-456-7890)");

      } catch (e) {
        this.errors.push("Target phone number required");
      }

      if (this.initialMessage && this.initialMessage.value === "--custom--" && !this.customMessage) {
        this.errors.push("Please enter opening message");
      }

      if (this.errors.length > 0)
        return false;

      this.httpPending = true;

      const payload = {
        recipients: [{
          number: this.dialCode + this.recipientNumber
        }]
      };

      if (this.initialMessage) {
        if (this.initialMessage.value === "--custom--")
          payload["message"] = this.customMessage;
        else
          payload["message"] = this.initialMessage.text;
      }

      if (this.sourceNumber && this.sourceNumber.number)
        payload["number"] = this.sourceNumber.number;

      if (this.targetDialect)
        payload["language"] = this.targetDialect.code;

      try {
        const resp = await this.$ConveyApi.conversationsApi.createConversation({ payload });

        if (resp.status <= 201) {
          EventBus.$emit("close-modal");
          if (resp.status == 201) {
            this.$toastr("success", "New Conversation Created");
            this.createConversation(resp.data.data);

            if (payload["number"] !== null) {
              this.$cookie.set("x-pref-sourcenumber", payload["number"]);
            }

          } else
            this.$toastr("warning", "Showing active conversation", "Active Conversation Exists");

          // this.formattedRecipientNumber = null;
          // this.targetDialect = null;
          // this.initialMessage = null;

          if (this.providerImport)
            this.$store.commit("provider/unsetProviderImport");

          this.$router.push({ path: `/conversations/${resp.data.data.conversation_id}` }).catch(() => { });
          this.httpPending = false;

          return true;
        } else {
          this.$toastr("error", "Error Creating Conversation (" + resp.status + ")");
          this.httpPending = false;
          return false;
        }
      } catch (error) {
        /* eslint-disable */
        console.error(error);
        /* eslint-enable */
        this.$toastr("error", "Error Creating Conversation (298)");
        this.httpPending = false;
        return false;
      }
    },
    focusOut: function (event) {
      if (["Arrow", "Backspace", "Shift"].includes(event.key)) {
        this.preventNextIteration = true;
        return;
      }
      if (this.preventNextIteration) {
        this.preventNextIteration = false;
        return;
      }

      try {
        if (this.formattedRecipientNumber && this.formattedRecipientNumber.length > 0)
          this.recipientNumber = this.formattedRecipientNumber.replace(/[^0-9]/g, "").match(/(\d{1,10})/g)[0];
      } catch (err) {
        /* eslint-disable */
        console.log(err);
        /* eslint-enable */
      }

      if (this.recipientNumber && this.recipientNumber.length == 3)
        this.formattedRecipientNumber = this.recipientNumber + "-";
      if (this.recipientNumber && this.recipientNumber.length == 6)
        this.formattedRecipientNumber = this.recipientNumber.replace(/^(\d{1,3})(\d{1,3})$/g, "$1-$2-");
    },
    initFocus() {
      const $ = window.$;
      setTimeout(function () {
        $("#id-recipientNumber").focus();
      }, 250);
    }
  },
  mounted() {
    this.$nextTick(() => this.initFocus());
  },
  created() {
    this.fetchTemplates();
    this.fetchNumbers();
    EventBus.$on("actionButton-click", () => {
      this.validateForm();
    });
    this.dialCodeSelection = ["+1", "+61", "+44", "+91"];
    if (document.getElementById("id-new-convo-form"))
      document.getElementById("id-new-convo-form").reset();

    if (this.providerImport && this.providerImport.number) {
      const importedNumber = this.providerImport.number;
      this.recipientNumber = importedNumber.substr(2);

      if (this.providerImport.latest_geolocation) {
        this.lat = this.providerImport.latest_geolocation.lat;
        this.lng = this.providerImport.latest_geolocation.lng;
        this.latlng_accuracy = this.providerImport.latest_geolocation.latlng_accuracy;
        this.altitude = this.providerImport.latest_geolocation.altitude;
        this.altitude_accuracy = this.providerImport.latest_geolocation.altitude_accuracy;
        this.approximateAddress = this.providerImport.latest_geolocation.normalized_address;
      }
      this.formattedRecipientNumber = importedNumber.substr(2, 3) + "-" + importedNumber.substr(5, 3) + "-" + importedNumber.substr(8, 4);
      if (this.providerImport.language)
        this.targetDialect = this.getTextDialects.filter(lang => lang.code == this.providerImport.language);
    }
  }
};
</script>

<style scoped>
.stretch-footer {
  margin-left: -16px;
  margin-right: -16px;
  margin-bottom: -16px;
}
</style>